import * as calculatorConfig from '@/config/base-config.json';
import * as config from '@/config/config.json';
import { generateSliderSteps } from '@/utils/generateSliderSteps';
import { EC_FINANCING_YEAR_65, EC_FINANCING_YEAR_75, FinancingLoanType } from '../common';

export const MAX_PROFILES = 4;
export const MSR_LIMIT = config.msr_rate;

export const MSR_LABEL = {
  Age: 'Age',
  EnterAge: 'Enter age',
  AvailableCash: 'Available Cash',
  AvailableCpfOa: 'Available CPF OA',
  IncomeAssessment: 'Income Assessment',
  MonthlyFixedIncome: 'Monthly Fixed Income',
  MonthlyVariableIncome: 'Monthly Variable Income',
  MonthlyVariableIncomeTooltip:
    'This includes rental income, commissions, and bonuses (recognized at',
  TotalMonthlyAssessableIncome: 'Total Monthly Assessable Income',
  DebtAssessment: 'Debt Assessment',
  MonthlyCarLoanInstalment: 'Monthly Car Loan Instalment',
  OtherMonthlyDebt: 'Other Monthly Debt',
  TotalMonthlyDebtAssessment: 'Total Monthly Debt Assessment',
  FinancingVariables: 'Financing Variables',
  LoanTenure: 'Loan Tenure (Years)',
  MediumTermInterestRate: 'Medium-Term Interest Rate',
  LoanToValue: 'Loan-to-Value (LTV)',
  AffordabilitySummary: 'Affordability Summary',
  IncomeWeightedAverageAge: 'Income Weighted Average Age',
  MaxLoanTenure: 'Max Loan Tenure',
  TotalCashAvailable: 'Total Cash Available',
  TotalCpfOaAvailable: 'Total CPF OA Available',
  CombinedMonthlyAssessableIncome: 'Combined Monthly Assessable Income',
  MSRLimit: 'MSR Limit',
  MaxLoanAmount: 'Max Loan Amount',
  MaxPurchasePrice: 'Max Purchase Price',
  MaxPurchasePriceBreakdown: 'Max Purchase Price Breakdown',
  BasedOnIncome: 'Based on Income',
  Cash: 'Cash',
  CpfOaPerCash: 'CPF OA / Cash',
  LoanAmount: 'Loan Amount',
  PlusAddProfile: '+ Add Profile',
  AdditionalProfile: 'Additional Profile',
  PropertyType: 'Property Type',
};

export enum PropertyType {
  HDB = 'HDB',
  EC = 'EC',
}

export const MEDIUM_TERM_INTEREST_RATE = {
  LABEL_INPUTS: generateSliderSteps(
    calculatorConfig.medium_term_interest_rate.min,
    calculatorConfig.medium_term_interest_rate.max,
    0.2,
  ),
  DEFAULT: calculatorConfig.medium_term_interest_rate.default,
  MIN: calculatorConfig.medium_term_interest_rate.min,
  MAX: calculatorConfig.medium_term_interest_rate.max,
  STEP: 0.01,
};

export const DEFAULT_HDB_LOAN_DETAILS_AGE_65 = {
  LOAN_TYPE: FinancingLoanType.sixtyFiveYearLoan,
  LOAN_TENURE_MAX: config.max_loan_tenure_in_years.hdb.bank_loan.upto_65_financing_year,
  LOAN_TENURE_LABEL: generateSliderSteps(
    0,
    config.max_loan_tenure_in_years.hdb.bank_loan.upto_65_financing_year,
    10,
  ),
  LOAN_TO_VALUE_MAX: config.max_ltv_in_percent.hdb.upto_65_financing_year,
  LOAN_TO_VALUE_LABEL: generateSliderSteps(
    0,
    config.max_ltv_in_percent.hdb.upto_65_financing_year,
    15,
  ),
};

export const DEFAULT_HDB_LOAN_DETAILS_AGE_75 = {
  LOAN_TYPE: FinancingLoanType.seventyFiveYearLoan,
  LOAN_TENURE_MAX: config.max_loan_tenure_in_years.hdb.bank_loan.upto_75_financing_year,
  LOAN_TENURE_LABEL: generateSliderSteps(
    0,
    config.max_loan_tenure_in_years.hdb.bank_loan.upto_75_financing_year,
    10,
  ),
  LOAN_TO_VALUE_MAX: config.max_ltv_in_percent.hdb.upto_75_financing_year,
  LOAN_TO_VALUE_LABEL: generateSliderSteps(
    0,
    config.max_ltv_in_percent.hdb.upto_75_financing_year,
    15,
  ),
};

export const DEFAULT_EC_LOAN_DETAILS_AGE_65 = {
  LOAN_TYPE: FinancingLoanType.sixtyFiveYearLoan,
  LOAN_TENURE_MAX: EC_FINANCING_YEAR_65.LOAN_TENURE.MAX,
  LOAN_TENURE_LABEL: EC_FINANCING_YEAR_65.LOAN_TENURE.SLIDER_LABELS,
  LOAN_TO_VALUE_MAX: EC_FINANCING_YEAR_65.LTV.MAX,
  LOAN_TO_VALUE_LABEL: EC_FINANCING_YEAR_65.LTV.SLIDER_LABELS,
};

export const DEFAULT_EC_LOAN_DETAILS_AGE_75 = {
  LOAN_TYPE: FinancingLoanType.seventyFiveYearLoan,
  LOAN_TENURE_MAX: EC_FINANCING_YEAR_75.LOAN_TENURE.MAX,
  LOAN_TENURE_LABEL: EC_FINANCING_YEAR_75.LOAN_TENURE.SLIDER_LABELS,
  LOAN_TO_VALUE_MAX: EC_FINANCING_YEAR_75.LTV.MAX,
  LOAN_TO_VALUE_LABEL: EC_FINANCING_YEAR_75.LTV.SLIDER_LABELS,
};

export const BREAKDOWN_COLORS = {
  cash: '#F8CB59',
  cpfOaOrCash: '#9475EB',
  loan: '#F25A73',
};

export type Profile = {
  name: string;
  age: number;
  availableCash: number;
  availableCPFOA: number;
  monthlyFixedIncome: number;
  monthlyVariableIncome: number;
  monthlyCarLoanInstalment: number;
  monthlyOtherDebt: number;
};
